import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { SegmentListCounts } from "../api/useSegmentListCounts";
import { useSegmentTabVacancies } from "../api/useSegmentTabVacancies";
import { useSegmentVacancyTotal } from "../api/useSegmentVacancyTotal";
import {
  VACANCY_LIST,
  VACANCY_OVERRIDE,
  type VacancyListKind,
  type VacancyOverrideType,
} from "../constants";
import { useUndoToast } from "../hooks/useUndoToast";
import { useVacancyFilterSections } from "../hooks/useVacancyFilterSections";
import { useVacancyListParams } from "../hooks/useVacancyListParams";
import { isSegmentCountable, type DraftBody } from "../mappers";
import {
  findOverride,
  flipOverride,
  removeOverrides,
  revertOverrides,
} from "../overrides";
import type { SegmentFormValues } from "../schemas";
import { AnimatedCount } from "./AnimatedCount";
import { Collapse } from "./Collapse";
import { SegmentVacancyDrawer } from "./SegmentVacancyDrawer";
import { VacanciesFiltersDrawer } from "./VacanciesFiltersDrawer";
import { VacanciesToolbar } from "./VacanciesToolbar";
import { VacancyList } from "./VacancyList";

import { Badge } from "@/components/ui/v2/badge";
import { SegmentedControl } from "@/components/ui/v2/segmented-control";
import {
  TableActiveFilters,
  type ActiveFilter,
} from "@/components/ui/v2/table/table-active-filters";
import { useDrawer } from "@/components/ui/v2/use-drawer";
import { SEGMENT_TOUR_TARGET } from "@/features/segments/tour/constants";
import type { SegmentVacancyItem } from "@/features/segments/types";
import { useTourTarget } from "@/features/tours/useTourTarget";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";

type VacanciesTabProps = {
  clientId?: string;
  readOnly: boolean;
  draftBody: DraftBody;
  listCounts: SegmentListCounts;
};

export function VacanciesTab({
  clientId,
  readOnly,
  draftBody,
  listCounts,
}: VacanciesTabProps) {
  const { t } = useTranslation();
  const { watch, setValue, getValues } = useFormContext<SegmentFormValues>();
  const overrides = watch("vacancyOverrides");
  const params = useVacancyListParams();
  const { searchValue, debouncedSearchValue, setSearchValue } =
    useSearchSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { openDrawer } = useDrawer();

  const {
    query: vacancies,
    items,
    totalCount,
  } = useSegmentTabVacancies({
    body: draftBody,
    overrides,
    list: params.list,
    search: debouncedSearchValue,
    descending: params.descending,
    filters: params.filters,
  });

  const firstVacancyRef = useTourTarget<HTMLDivElement>(
    SEGMENT_TOUR_TARGET.VACANCIES,
  );
  const emptyVacanciesRef = useTourTarget<HTMLDivElement>(
    SEGMENT_TOUR_TARGET.VACANCIES,
    items.length === 0,
  );
  const firstFlipRef = useTourTarget<HTMLButtonElement>(
    SEGMENT_TOUR_TARGET.VACANCY_FLIP,
  );
  const emptyFlipRef = useTourTarget<HTMLDivElement>(
    SEGMENT_TOUR_TARGET.VACANCY_FLIP,
    items.length === 0,
  );
  const vacancyListsRef = useTourTarget<HTMLDivElement>(
    SEGMENT_TOUR_TARGET.VACANCY_LISTS,
  );
  const filterScope = {
    body: draftBody,
    list: params.list,
    search: debouncedSearchValue,
  };
  const { sections: filterSections, isLoading: filtersLoading } =
    useVacancyFilterSections({ ...filterScope, enabled: filtersOpen });
  const useFilteredCount = (filters: Record<string, string[]>) =>
    useSegmentVacancyTotal({ ...filterScope, filters, enabled: filtersOpen });
  const hasQuery =
    Boolean(debouncedSearchValue) || params.activeFilterCount > 0;
  const activeFilters: ActiveFilter[] = filterSections.flatMap((section) =>
    params.filters[section.key].map((value) => ({
      key: section.key,
      label: section.title,
      value,
      valueLabel:
        section.options.find((option) => option.value === value)?.label ??
        value,
    })),
  );

  const setOverrides = (next: SegmentFormValues["vacancyOverrides"]) =>
    setValue("vacancyOverrides", next, { shouldDirty: true });
  const showUndoToast = useUndoToast();

  const getOverrideToastMessage = (type: VacancyOverrideType | undefined) => {
    if (type === VACANCY_OVERRIDE.INCLUDE)
      return t("admin.segments.builder.exceptions.includedToast");
    if (type === VACANCY_OVERRIDE.EXCLUDE)
      return t("admin.segments.builder.exceptions.excludedToast");
    return t("admin.segments.builder.exceptions.exceptionRemoved", {
      count: 1,
    });
  };

  const changeOverride = (
    item: SegmentVacancyItem,
    next: SegmentFormValues["vacancyOverrides"],
    includedDelta: number,
  ) => {
    const before = getValues("vacancyOverrides");
    const { vacancyId } = item.vacancy;
    setOverrides(next);
    listCounts.adjust(includedDelta, next);
    showUndoToast({
      message: getOverrideToastMessage(findOverride(next, vacancyId)),
      description:
        item.vacancy.title ?? t("admin.segments.builder.vacancies.untitled"),
      onUndo: () => {
        const restored = revertOverrides(
          getValues("vacancyOverrides"),
          before,
          [vacancyId],
        );
        setOverrides(restored);
        listCounts.adjust(-includedDelta, restored);
      },
    });
  };

  const countLabel = (
    value: VacancyListKind,
    label: string,
    count: number | undefined,
  ) => (
    <span className="inline-flex items-center gap-2">
      {label}
      {count !== undefined && (
        <Badge
          variant={params.list === value ? "secondary" : "default"}
          size="small"
          shape="pill"
          data-testid={`vacancy-list-count-${value}`}
        >
          <AnimatedCount value={count} />
        </Badge>
      )}
    </span>
  );

  const listOptions = [
    {
      value: VACANCY_LIST.INCLUDED,
      label: countLabel(
        VACANCY_LIST.INCLUDED,
        t("admin.segments.builder.vacancies.includes"),
        listCounts.data?.includedCount,
      ),
    },
    {
      value: VACANCY_LIST.EXCLUDED,
      label: countLabel(
        VACANCY_LIST.EXCLUDED,
        t("admin.segments.builder.vacancies.excluded"),
        listCounts.data?.excludedCount,
      ),
    },
  ];

  return (
    <div
      ref={emptyFlipRef}
      className="flex flex-col gap-4 py-6"
      data-testid="segment-vacancies-tab"
    >
      <VacanciesToolbar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        descending={params.descending}
        onDescendingChange={params.setDescending}
        activeFilterCount={params.activeFilterCount}
        onOpenFilters={() => setFiltersOpen(true)}
      />
      <SegmentedControl<VacancyListKind>
        label={t("admin.segments.builder.vacancies.listLabel")}
        value={params.list}
        onValueChange={params.setList}
        options={listOptions}
        rootRef={vacancyListsRef}
        className="flex w-full"
        itemClassName="flex-1"
      />
      <Collapse open={hasQuery} spacing={4}>
        <div className="flex items-start gap-4">
          {totalCount !== undefined && (
            <span
              className="shrink-0 py-1.5 text-sm font-medium"
              data-testid="vacancy-result-count"
            >
              {t("admin.filters.totalRecords", {
                count: totalCount,
                entity: t("common.vacancies"),
              })}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <TableActiveFilters
              filters={activeFilters}
              onRemoveFilter={(filter) =>
                params.removeFilter(filter.key, String(filter.value))
              }
              onClearFilters={params.clearFilters}
            />
          </div>
        </div>
      </Collapse>
      <div ref={emptyVacanciesRef}>
        <VacancyList
          list={params.list}
          items={items}
          overrides={overrides}
          readOnly={readOnly}
          isLoading={vacancies.isLoading}
          isFetching={vacancies.isFetching}
          isError={vacancies.isError}
          hasMore={vacancies.hasNextPage}
          isLoadingMore={vacancies.isFetchingNextPage}
          onLoadMore={vacancies.fetchNextPage}
          hasQuery={hasQuery}
          isIncomplete={!isSegmentCountable(draftBody)}
          onFlip={(item) =>
            changeOverride(
              item,
              flipOverride(
                getValues("vacancyOverrides"),
                item.vacancy.vacancyId,
                item.matchesRules,
              ),
              params.list === VACANCY_LIST.INCLUDED ? -1 : 1,
            )
          }
          onRemoveOverride={(item) =>
            changeOverride(
              item,
              removeOverrides(getValues("vacancyOverrides"), [
                item.vacancy.vacancyId,
              ]),
              0,
            )
          }
          firstItemRef={firstVacancyRef}
          firstFlipRef={firstFlipRef}
          onOpen={(item) =>
            openDrawer(
              <SegmentVacancyDrawer
                clientId={clientId}
                vacancyId={item.vacancy.vacancyId}
              />,
              t("admin.segments.builder.vacancyDetails"),
              "medium",
            )
          }
        />
      </div>
      <VacanciesFiltersDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filterSections={filterSections}
        filtersLoading={filtersLoading}
        searchTag={debouncedSearchValue}
        useFilteredCount={useFilteredCount}
      />
    </div>
  );
}
